import { clearAiCampaignDraft } from '@/features/campaigns/utils/aiCampaignDraftStorage';
import { clearStandardCampaignDraft } from '@/features/campaigns/utils/standardCampaignDraftStorage';
import { routes } from '@/shared/config/routes';

const FRESH_TOKEN_KEY = 'screenista.campaign.freshToken';
const REPETITION_TYPE_KEY = 'screenista.campaign.repetitionType';

export type RepetitionType = 'random' | 'screen_wise';

export type FreshCampaignNavState = {
  fresh: true;
  freshId: number;
  repetitionType?: RepetitionType;
};

/** One-shot token: survives navigate, consumed on first mount, gone after refresh. */
export function armFreshCampaignStart(freshId: number) {
  try {
    sessionStorage.setItem(FRESH_TOKEN_KEY, String(freshId));
  } catch {
    // ignore
  }
}

/** Returns true only once per New Campaign click — not on browser refresh. */
export function consumeFreshCampaignStart(freshId?: number): boolean {
  if (freshId == null) return false;
  try {
    const armed = sessionStorage.getItem(FRESH_TOKEN_KEY);
    if (armed && armed === String(freshId)) {
      sessionStorage.removeItem(FRESH_TOKEN_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export function getStoredRepetitionType(): RepetitionType {
  try {
    const val = sessionStorage.getItem(REPETITION_TYPE_KEY);
    if (val === 'screen_wise') return 'screen_wise';
  } catch {
    // ignore
  }
  return 'random';
}

/** Persist schedule mode so Step 3 can switch without leaving the wizard. */
export function setStoredRepetitionType(repetitionType: RepetitionType) {
  try {
    sessionStorage.setItem(REPETITION_TYPE_KEY, repetitionType);
  } catch {
    // ignore
  }
}

/** Clears previous drafts and navigates to a blank create flow (forces remount). */
export function startFreshCampaignCreate(
  navigate: (
    to: string,
    opts?: { state?: FreshCampaignNavState; replace?: boolean },
  ) => void,
  mode: 'ai' | 'standard',
  repetitionType: RepetitionType = 'random',
) {
  const freshId = Date.now();
  clearStandardCampaignDraft();
  clearAiCampaignDraft();
  armFreshCampaignStart(freshId);
  try {
    sessionStorage.setItem(REPETITION_TYPE_KEY, repetitionType);
  } catch {
    // ignore
  }
  navigate(routes.campaignCreateMode(mode), {
    state: { fresh: true, freshId, repetitionType },
  });
}
